// Flattens a Note of Presentation into the plain text the AI spelling/notes
// check reads.
//
// Subject, each reference's description, and the body are stored as Jodit rich
// text (HTML), so they cannot be concatenated raw: the model would spend
// tokens on markup and report tag fragments as misspellings. Attachment and
// other-attachment labels are plain strings already.
//
// Only text is included. Reference and other-attachment *files* (pdf/docx) are
// deliberately left out — checking inside those needs the document-parsing
// path, not this one.

// Uses the browser's own parser rather than a tag-stripping regex: these
// documents are Khmer text with nested inline formatting, where a regex
// reliably mangles entities (&nbsp;, &amp;) and leaves attribute text behind.
function htmlToText(html) {
	if (!html) return '';
	if (typeof window === 'undefined' || !window.DOMParser) {
		return String(html).replace(/<[^>]*>/g, ' ');
	}
	const doc = new DOMParser().parseFromString(String(html), 'text/html');
	// Block-level elements carry the line breaks a reader sees; without this the
	// body collapses into one run-on line and the AI's `context` snippets stop
	// lining up with anything recognizable.
	doc.body.querySelectorAll('p, div, br, li, tr, h1, h2, h3, h4, h5, h6').forEach((el) => {
		el.insertAdjacentText('afterend', '\n');
	});
	const text = doc.body.textContent || '';
	return text
		.replace(/ /g, ' ')
		.replace(/[ \t]+/g, ' ')
		.replace(/\n{3,}/g, '\n\n')
		.split('\n')
		.map((line) => line.trim())
		.join('\n')
		.trim();
}

// Builds the labelled, section-by-section text. Sections are labelled so the
// AI's `context` snippets point at an identifiable part of the record, and so
// a note like "addressee does not match the body" has something to refer to.
function buildNpCheckText(npData) {
	if (!npData) return '';
	const sections = [];

	const subject = htmlToText(npData?.subject);
	if (subject) sections.push(`Subject:\n${subject}`);

	const references = (npData?.references || []).map((reference) => htmlToText(reference?.description)).filter(Boolean);
	if (references.length) {
		sections.push(`Reference:\n${references.map((line) => `- ${line}`).join('\n')}`);
	}

	// `attachings` backs the "Attachment" section and holds rich-text
	// `description`; `attachments` backs "Other Attachments" and holds a plain
	// `label`. The names read the other way round, so they are easy to swap by
	// mistake — see np-editor/index.js:255-256, which is the authority here.
	const attachings = (npData?.attachings || []).map((attaching) => htmlToText(attaching?.description)).filter(Boolean);
	if (attachings.length) {
		sections.push(`Attachment:\n${attachings.map((line) => `- ${line}`).join('\n')}`);
	}

	const description = htmlToText(npData?.description);
	if (description) sections.push(`Content Detail:\n${description}`);

	const attachments = (npData?.attachments || []).map((attachment) => (attachment?.label || '').trim()).filter(Boolean);
	if (attachments.length) {
		sections.push(`Other Attachments:\n${attachments.map((label) => `- ${label}`).join('\n')}`);
	}

	return sections.join('\n\n');
}

// Applies an accepted correction to a rich-text (HTML) field by rewriting only
// the text nodes, so inline formatting, attributes and entities survive — a
// string replace over the raw HTML would happily corrupt a tag whose attribute
// happens to contain the same characters as the misspelling.
function replaceInHtml(html, original, suggestion) {
	if (!html || !original) return html;
	if (typeof window === 'undefined' || !window.DOMParser) return html;

	const doc = new DOMParser().parseFromString(String(html), 'text/html');
	const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
	let changed = false;
	const nodes = [];
	while (walker.nextNode()) nodes.push(walker.currentNode);

	nodes.forEach((node) => {
		if (!node.nodeValue || node.nodeValue.indexOf(original) === -1) return;
		// Shares the plain-text replacer, which skips occurrences that already
		// read as the correction — needed because a dropped-final-character
		// typo is a prefix of its own fix.
		const replaced = replaceTextOccurrences(node.nodeValue, original, suggestion);
		if (replaced !== node.nodeValue) {
			node.nodeValue = replaced;
			changed = true;
		}
	});

	return changed ? doc.body.innerHTML : html;
}

// Applies every accepted correction across all of a note's text-bearing
// fields, returning the fields to save. Untouched fields come back unchanged
// so the caller can hand the whole set to saveEditNp without tracking which
// ones the corrections happened to land in.
//
// Plain-string label fields (attachments/attachings) are replaced directly;
// only the rich-text fields go through the DOM-aware path.
// Replaces `original` with `suggestion` everywhere in a plain string, but
// skips occurrences that are already the corrected form.
//
// This matters because Khmer typos are very often a dropped final character,
// which makes the misspelling a strict PREFIX of the correction (លិខិតអញ្ជើ →
// លិខិតអញ្ជើញ). A naive split/join would then also match inside every
// already-correct instance and append the character again (…ញញ), corrupting
// text that was fine.
function replaceTextOccurrences(value, original, suggestion) {
	if (!value || !original || original === suggestion) return value;

	let result = '';
	let index = 0;
	while (index < value.length) {
		const found = value.indexOf(original, index);
		if (found === -1) {
			result += value.slice(index);
			break;
		}
		// Text that already reads as the suggestion is not a typo. Either way
		// the output here is `suggestion`; what differs is how far to advance —
		// past the whole correct form, so the shorter `original` cannot match
		// inside it a second time.
		const alreadyCorrect = value.startsWith(suggestion, found);
		result += value.slice(index, found) + suggestion;
		index = found + (alreadyCorrect ? suggestion.length : original.length);
	}
	return result;
}

function applyCorrectionsToNp(npData, corrections) {
	const applyText = (value) =>
		corrections.reduce((acc, correction) => {
			if (!acc || !correction?.original) return acc;
			return replaceTextOccurrences(acc, correction.original, correction.suggestion);
		}, value || '');

	const applyHtml = (value) => corrections.reduce((acc, correction) => replaceInHtml(acc, correction?.original, correction?.suggestion), value || '');

	return {
		subject: applyHtml(npData?.subject),
		description: applyHtml(npData?.description),
		references: (npData?.references || []).map((reference) => ({
			...reference,
			description: applyHtml(reference?.description),
		})),
		// "Other Attachments" — plain `label` strings.
		attachments: (npData?.attachments || []).map((attachment) => ({
			...attachment,
			label: applyText(attachment?.label),
		})),
		// "Attachment" — rich-text `description`, so it needs the DOM-aware path.
		attachings: (npData?.attachings || []).map((attaching) => ({
			...attaching,
			description: applyHtml(attaching?.description),
		})),
	};
}

// Every attachment file on a note worth parsing, across the three places files
// can hang: reference rows, the Attachment section (attachings) and Other
// Attachments (attachments). Each can carry both an original office `file` and
// a generated `pdf`.
//
// Prefers the .docx and SKIPS the PDF when a row has both. They are the same
// document, but .docx extracts locally for free and losslessly, while a PDF is
// read by the model and billed per page — so reading the PDF too would be
// paying for a second copy of text already in hand.
//
// A PDF is only used when the row has no .docx alternative; without it, notes
// whose attachments are scans would contribute nothing.
//
// Returns `{ url, label }`, where label is the section plus the row's own text
// — e.g. `Reference[លិខិតលេខ ០៥-២០២៦-០០៧ …]`. The AI quotes that label back in
// its notes, so a reviewer can tell which file a finding is about.
function collectNpAttachmentUrls(npData) {
	const files = [];
	const seen = new Set();
	const isDocx = (url) => (url || '').split('?')[0].toLowerCase().endsWith('.docx');

	// Truncated so one long reference line cannot dominate a note's wording.
	// Long enough to stay identifiable — a letter number and its subject.
	const shortLabel = (value) => {
		const clean = (value || '').replace(/\s+/g, ' ').trim();
		if (!clean) return '';
		return clean.length > 90 ? `${clean.slice(0, 90)}…` : clean;
	};

	const push = (item, section, rawLabel) => {
		const officeUrl = item?.file?.url;
		const pdfUrl = item?.pdf?.url;

		// Prefer the .docx: same document, but parsed locally for free rather
		// than read (and billed) page by page as a PDF.
		let url = officeUrl && isDocx(officeUrl) ? officeUrl : officeUrl || pdfUrl;
		if (!url || seen.has(url)) return;
		seen.add(url);

		const label = shortLabel(rawLabel);
		files.push({ url, label: label ? `${section}[${label}]` : section });
	};

	// Each section stores its human label in a different field: references and
	// attachings carry rich-text `description`, Other Attachments a plain
	// `label`. Labelling by section + text is what lets a reviewer find the
	// offending file; a bare "Reference 1" does not.
	(npData?.references || []).forEach((item) => push(item, 'Reference', htmlToText(item?.description)));
	(npData?.attachings || []).forEach((item) => push(item, 'Attachment', htmlToText(item?.description)));
	(npData?.attachments || []).forEach((item) => push(item, 'Other Attachments', item?.label));

	return files;
}

export { htmlToText, buildNpCheckText, replaceInHtml, applyCorrectionsToNp, collectNpAttachmentUrls };
